import React, { Fragment, useMemo } from "react";
import { useQuery, gql } from "urql";
import { Link } from "wouter";

import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { Button } from "../../../components/src/Button";
import { SelectColumnFilter, SelectColumnFilterArrayOfIds } from "../../../components/src/react-table/ReactTableFilters";
import { CollectionNameEnum } from "../types.generated";

export const QUERY_EXTENSIONS = gql`
    query extensions($filter: ExtensionsFilterInput) {
        extensions(filter: $filter) {
            _id
            clientId
            client {
                name
            }
            collection
            documentId
            status
            key
            value
            createTimestamp
            updateTimestamp
            updateUserId
            updateUserInfo {
                name
            }
        }
    }
`;

export const ExtensionsPage = (): React.ReactElement => {
    const [{ fetching, error, data }] = useQuery({ query: QUERY_EXTENSIONS });

    const extensions = useMemo(() => data?.extensions ?? [], [data]);

    const columns = useMemo(() => {
        const clientsById: Record<string, { _id: string; name: string }> = {};
        extensions.forEach((ext: { clientId: string; client: { name: string } }) => {
            if (ext.clientId && ext.client?.name && !(ext.clientId in clientsById)) {
                clientsById[ext.clientId] = { _id: ext.clientId, name: ext.client.name };
            }
        });

        return [
            {
                header: "_id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/extensions/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 75
            },
            {
                header: "client",
                id: "client.name",
                accessorKey: "clientId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original.clientId}`}>{row.original.client.name}</Link>;
                },
                size: 110,
                filter: SelectColumnFilterArrayOfIds(Object.values(clientsById))
            },
            {
                header: "collection",
                accessorKey: "collection",
                filter: SelectColumnFilter
            },
            {
                header: "document",
                id: "documentId",
                accessorKey: "documentId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.collection === CollectionNameEnum.Instrument)
                        return <Link to={`/instruments/${row.original.documentId}`}>{row.original.documentId}</Link>;
                    else if (row.original.collection === CollectionNameEnum.Party)
                        return <Link to={`/parties/${row.original.documentId}`}>{row.original.documentId}</Link>;
                    else return <div>{row.original.documentId}</div>;
                },
                size: 110
            },
            {
                header: "key",
                accessorKey: "key"
            },
            {
                header: "value",
                accessorKey: "value"
            },
            {
                header: "createTimestamp",
                accessorKey: "createTimestamp"
            },
            {
                header: "updateTimestamp",
                accessorKey: "updateTimestamp"
            },
            {
                header: "updateUser",
                accessorKey: "updateUserInfo",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <div>{row.original.updateUserInfo.name}</div>;
                }
            }
        ];
    }, [extensions]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    return (
        <div>
            <Fragment>
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2">
                        <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className="float-right"
                            onClick={() => {
                                const win = window.open("/extensions/new", "_self");
                                win.focus();
                            }}
                        >
                            New extension
                        </Button>
                    </div>
                </div>
                <p>Number of extensions: {extensions.length}</p>
                <ReactTable columns={columns} data={extensions} />
            </Fragment>
        </div>
    );
};
